/**
 * What an upload's progress stream says, as data the reader's locale words
 * (uiv2 7b·S2).
 *
 * Measured at e15ac20: `/transactions/upload` wrote 41 English sentences into
 * its SSE `message` and `/invoices/upload` 27, and both progress modals printed
 * them ahead of their own translated descriptions — so a French or Arabic
 * reader watched every import in English. The server now sends a KEY from this
 * table and the values it names; the web words `uploadProgressMessages.<key>`.
 *
 * Each entry lists the parameters its sentence takes, and nothing else: the
 * API's `progressMessage()` is typed against it (a stray sentence or a missing
 * value fails `tsc`), and the web's translation test holds every locale's
 * placeholders to it. Numbers go raw and are formatted by the client; a name
 * (a file, a vendor) is data and is printed inside the translated sentence.
 * `docType` is a code from `UPLOAD_DOC_TYPES`, worded by the client too.
 * No count is named `count`: the sentences put a count after a label
 * ("Transactions found: 12"), which reads right in every locale without
 * i18next's plural families.
 */
export const UPLOAD_PROGRESS_MESSAGES = {
    // ── /transactions/upload ───────────────────────────────────────────────────
    received: ['file'],
    reimporting: ['n'],
    compareAnalyzing: ['n'],
    revalidateAnalyzing: ['n'],
    detectingType: [],
    maybeWrongType: ['docType'],
    userOverride: [],
    typeUnclear: [],
    verifiedStatement: [],
    extracting: [],
    learnedRules: [],
    parsingCsv: [],
    extracted: ['n'],
    extractedDirect: ['n'],
    totalsMatch: [],
    flippingSigns: [],
    signsFlipped: [],
    forceImport: ['debitPercent', 'creditPercent'],
    totalsMismatch: ['debitGap', 'creditGap'],
    dateErrors: ['n'],
    correctingDates: ['n'],
    datesCorrected: ['n'],
    billerCheck: [],
    aiCategorizing: ['n'],
    aiBatch: ['current', 'total'],
    categorized: ['n', 'billers', 'ai'],
    categorizedWithFailures: ['n', 'billers', 'ai', 'failed'],
    comparing: [],
    comparingProgress: ['done', 'total'],
    revalidating: ['n'],
    revalidatingProgress: ['done', 'total'],
    revalidated: ['updated', 'unchanged'],
    saving: ['n'],
    savingProgress: ['done', 'total'],
    saved: ['n', 'flagged'],
    billersCategorizing: ['n'],
    billersCategorized: ['done', 'total'],
    billersFailed: [],
    noNewBillers: [],
    driveUploading: [],
    driveUploaded: [],
    driveFailed: [],
    imported: ['n'],
    // ── /invoices/upload ───────────────────────────────────────────────────────
    validatingFiles: [],
    aiNotConfigured: [],
    startingAi: [],
    startingInvoices: [],
    invoiceReading: ['file'],
    invoiceOcr: ['docType'],
    invoiceExtracted: [],
    lineItemsProcessing: [],
    lineItemsValidated: ['n'],
    vendorMatching: [],
    vendorMatched: ['name'],
    vendorNew: ['name'],
    vendorMatchingDone: [],
    billerCreated: ['name'],
    billerNotCreated: [],
    billerKnown: [],
    duplicateCheckDone: [],
    invoiceSaving: [],
    invoiceSaved: [],
    allFailed: ['n'],
    someProcessed: ['done', 'total'],
    allProcessed: ['n'],
    uploadFailed: [],
};
export const UPLOAD_PROGRESS_MESSAGE_KEYS = Object.keys(UPLOAD_PROGRESS_MESSAGES);
export function isUploadProgressMessageKey(key) {
    return typeof key === 'string' && Object.hasOwn(UPLOAD_PROGRESS_MESSAGES, key);
}
/** The document types a classifier can name; the `docType` parameter is one of them. */
export const UPLOAD_DOC_TYPES = ['bank_statement', 'invoice', 'credit_memo', 'debit_memo', 'receipt', 'unknown'];
/**
 * What a statement's date check concluded, so the summary branches on a code.
 * `UploadSummaryModal` used to decide info-vs-warning with
 * `warningMessage.includes('Auto-corrected')` — true only in English.
 */
export const DATE_VALIDATION_OUTCOMES = ['errors', 'warnings', 'autoCorrected'];
export const DATE_ANOMALY_TYPES = ['year_mismatch', 'outside_period', 'future_date', 'suspicious_year'];
/** What a compare-mode re-upload did with the statement's balances. */
export const BALANCE_EXTRACTION_OUTCOMES = ['extracted', 'calculatedPdf', 'calculatedCsv', 'failed'];
//# sourceMappingURL=upload-progress.js.map